import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Plus, Search, Filter, Download, Trash2, Eye, DollarSign, Upload, X,
  Calendar, Building, Edit, ArrowUp, ArrowDown
} from 'lucide-react';

import {
  format, parseISO, addDays, addMonths,
  startOfDay, endOfDay, isWithinInterval,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  isSameDay, isSameMonth
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import LancamentoDetailModal from '../components/LancamentoDetailModal';

const fmtBRL = (n) =>
  `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

// --- Normalizador de "Empresa/Entidade" p/ qualquer origem ---
const pickStr = (...vals) => {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const empresaFromNota = (l, tipo) => {
  const emit = pickStr(
    l.emitente, l.emitente_nome, l.emitente_razao,
    l.nota_emitente, l.nota_emitente_nome, l.nota_emitente_razao,
    l.nota?.emitente, l.nota?.emitente_nome, l.nota?.emitente_razao
  );
  const dest = pickStr(
    l.destinatario, l.destinatario_nome, l.destinatario_razao,
    l.nota_destinatario, l.nota_destinatario_nome, l.nota_destinatario_razao,
    l.nota?.destinatario, l.nota?.destinatario_nome, l.nota?.destinatario_razao
  );
  return (tipo === 'pagar') ? emit : dest;
};

const resolverEmpresa = (l) => {
  // 1) Se já veio pronto
  let emp = pickStr(l.empresa, l.empresa_nome, l.entidade, l.contraparte, l.parte_oposta);
  if (emp) return emp;

  // 2) Criados manualmente
  emp = (l.tipo === 'pagar')
    ? pickStr(l.fornecedor, l.fornecedor_nome)
    : pickStr(l.cliente, l.cliente_nome);
  if (emp) return emp;

  // 3) NF-e / CT-e
  if (l.nota_fiscal_id || l.nota) {
    emp = empresaFromNota(l, l.tipo);
    if (emp) return emp;
  }

  return 'Não informado';
};


const LancamentosV2 = () => {
  const [lancamentos, setLancamentos] = useState([]);
  const [filteredLancamentos, setFilteredLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLancamento, setSelectedLancamento] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentLancamento, setCurrentLancamento] = useState(null);

    // ✅ Filtros DESATIVADOS por padrão (mostra tudo)
  const [activeFilters, setActiveFilters] = useState({
    pagar: false,
    receber: false,
    vencida: false,
    vence_hoje: false,
    pendente: false,
    parcial: false,
    concluida: false
  });

  const [newLancamento, setNewLancamento] = useState({
    tipo: 'pagar',
    descricao: '',
    valor: '',
    data_vencimento: '',
    fornecedor: '',
    cliente: '',
    observacoes: ''
  });

  const [paymentData, setPaymentData] = useState({
    valor: '',
    data_pagamento: format(new Date(), 'yyyy-MM-dd'),
    observacoes: '',
    comprovante: null
  });

  const [focusedLancamentoId, setFocusedLancamentoId] = useState(null);

  // popover do calendário
const [showCalendar, setShowCalendar] = useState(false);
const calRef = useRef(null);
const focusLancamentoRef = useRef(null);

// fecha ao clicar fora do popover
useEffect(() => {
  const onDocClick = (e) => {
    if (!showCalendar) return;
    if (calRef.current && !calRef.current.contains(e.target)) {
      setShowCalendar(false);
    }
  };
  document.addEventListener('mousedown', onDocClick);
  return () => document.removeEventListener('mousedown', onDocClick);
}, [showCalendar]);


  // 📅 Filtro de calendário (um único cabeçalho)
const [dateFilter, setDateFilter] = useState({ from: '', to: '' }); // ISO yyyy-MM-dd
const [rangeStage, setRangeStage] = useState('from');               // 'from' | 'to'
const [quickRange, setQuickRange] = useState(null);                  // '7d' | '15d' | '30d' | 'mes' | null

const applyQuickRange = (key) => {
  const isUnselecting = quickRange === key;     // lê antes de setar
  setQuickRange(isUnselecting ? null : key);

  const hoje = new Date();

  if (isUnselecting) {
    // Desmarcou: limpa datas
    setDateFilter({ from: '', to: '' });
    setRangeStage('from');
    return;
  }

  if (key === 'mes') {
    const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    setDateFilter({
      from: format(inicio, 'yyyy-MM-dd'),
      to: format(fim, 'yyyy-MM-dd'),
    });
    setRangeStage('from');
    return;
  }

  const mapa = { '7d': 7, '15d': 15, '30d': 30 };
  const inicio = addDays(hoje, -mapa[key]);
  setDateFilter({
    from: format(inicio, 'yyyy-MM-dd'),
    to: format(hoje, 'yyyy-MM-dd'),
  });
  setRangeStage('from');
};


// Calendário único: 1º clique define "from", 2º clique define "to" (auto-swap se necessário)
const handleSingleCalendarPick = (value) => {
  if (!value) return;
  // Ao usar o calendário manual, desmarque presets
  if (quickRange) setQuickRange(null);

  if (rangeStage === 'from') {
    setDateFilter({ from: value, to: '' });
    setRangeStage('to');
  } else {
    if (dateFilter.from && value < dateFilter.from) {
      setDateFilter({ from: value, to: dateFilter.from });
    } else {
      setDateFilter({ from: dateFilter.from, to: value });
    }
    setRangeStage('from');
  }
};


const clearDateFilter = () => {
  setDateFilter({ from: '', to: '' });
  setRangeStage('from');
  setQuickRange(null);
};

  useEffect(() => {
    fetchLancamentos();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [lancamentos, searchTerm, activeFilters, dateFilter]);

  useEffect(() => {
    const pendingFocus = focusLancamentoRef.current;
    if (!pendingFocus) return;

    const match = filteredLancamentos.find((item) => String(item.id) === String(pendingFocus));
    if (match) {
      setSelectedLancamento(match);
      setShowDetailModal(true);
      focusLancamentoRef.current = null;
    }
  }, [filteredLancamentos]);

  const fetchLancamentos = async () => {
    try {
      const response = await fetch('/api/lancamentos');
      let payload;

      try {
        payload = await response.json();
      } catch (jsonError) {
        payload = null;
      }

      if (!response.ok) {
        const message =
          (payload && typeof payload === 'object' && payload !== null && 'error' in payload && payload.error) ||
          `Falha ao carregar lançamentos (status ${response.status})`;
        throw new Error(message);
      }

      const items = Array.isArray(payload) ? payload : [payload];

      const withEmpresa = items
        .filter((item) => item && typeof item === 'object' && !item.error)
        .map((l) => ({
          ...l,
          _empresa: resolverEmpresa(l),
        }));

      setLancamentos(withEmpresa);
    } catch (error) {
      console.error('Erro ao buscar lançamentos:', error);
      setLancamentos([]);
    } finally {
      setLoading(false);
    }
  };


const location = useLocation();

useEffect(() => {
  const qs = new URLSearchParams(location.search);

  const rawTipo = (qs.get('tipo') || '').toLowerCase();
  const rawStatus = qs.get('status') || '';
  const statusTokens = rawStatus
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  const statusSet = new Set(statusTokens);

  const venc = (qs.get('vencimento') || '').toLowerCase();
  const range = (qs.get('range') || '').toLowerCase();
  const alertDaysParam = qs.get('alertDays') || qs.get('alert_days');
  const alertDays = alertDaysParam ? parseInt(alertDaysParam, 10) : NaN;
  const dataInicio = qs.get('data_inicio') || qs.get('from') || '';
  const dataFim = qs.get('data_fim') || qs.get('to') || '';
  const singleDate = qs.get('date') || qs.get('dia') || '';
  const focusParam = qs.get('lancamentoId') || qs.get('lancamento_id') || qs.get('id');
  const focusId = typeof focusParam === 'string' ? focusParam.trim() : '';

  setActiveFilters({
    pagar: false,
    receber: false,
    vencida: false,
    vence_hoje: false,
    pendente: false,
    parcial: false,
    concluida: false,
  });

  if (rawTipo === 'pagar' || rawTipo === 'receber') {
    setActiveFilters((prev) => ({ ...prev, [rawTipo]: true }));
  }

  if (statusSet.has('vencida') || statusSet.has('atrasada') || statusSet.has('atrasadas')) {
    setActiveFilters((prev) => ({ ...prev, vencida: true }));
  }
  if (statusSet.has('pendente') || statusSet.has('pendentes')) {
    setActiveFilters((prev) => ({ ...prev, pendente: true }));
  }
  if (statusSet.has('parcial') || statusSet.has('parcialmente_pago')) {
    setActiveFilters((prev) => ({ ...prev, parcial: true }));
  }
  if (
    statusSet.has('concluida') ||
    statusSet.has('concluídas') ||
    statusSet.has('pago') ||
    statusSet.has('paga')
  ) {
    setActiveFilters((prev) => ({ ...prev, concluida: true }));
  }
  if (venc === 'hoje') {
    setActiveFilters((prev) => ({ ...prev, vence_hoje: true }));
  }

  const normalizedFrom = dataInicio || singleDate;
  const normalizedTo = dataFim || singleDate;

  if (normalizedFrom || normalizedTo) {
    setQuickRange(null);
    setDateFilter({
      from: normalizedFrom || normalizedTo,
      to: normalizedTo || normalizedFrom || '',
    });
    setRangeStage('from');
  } else if (['7d', '15d', '30d'].includes(range)) {
    const hoje = new Date();
    const dias = parseInt(range, 10);
    setQuickRange(range);
    setDateFilter({
      from: format(hoje, 'yyyy-MM-dd'),
      to: format(addDays(hoje, dias), 'yyyy-MM-dd'),
    });
    setRangeStage('from');
  } else if (range === 'mes') {
    const hoje = new Date();
    const inicio = startOfMonth(hoje);
    const fim = endOfMonth(hoje);
    setQuickRange('mes');
    setDateFilter({
      from: format(inicio, 'yyyy-MM-dd'),
      to: format(fim, 'yyyy-MM-dd'),
    });
    setRangeStage('from');
  } else if (!Number.isNaN(alertDays) && alertDays > 0) {
    const hoje = new Date();
    setQuickRange(null);
    setDateFilter({
      from: format(hoje, 'yyyy-MM-dd'),
      to: format(addDays(hoje, alertDays), 'yyyy-MM-dd'),
    });
    setRangeStage('from');
  } else {
    setQuickRange(null);
    setDateFilter({ from: '', to: '' });
    setRangeStage('from');
  }

  if (focusId) {
    setFocusedLancamentoId(focusId);
    focusLancamentoRef.current = focusId;
  } else {
    setFocusedLancamentoId(null);
    focusLancamentoRef.current = null;
  }
}, [location.search]);


  // ✅ NOVO: Calcular STATUS separado de PRAZO (retorna ARRAY de badges)
  const calcularStatusEPrazo = (lanc) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (!lanc?.data_vencimento) {
      return [];
    }

    let vencimento;
    try {
      vencimento = parseISO(lanc.data_vencimento);
    } catch (error) {
      console.warn('Data de vencimento inválida para lançamento', {
        lancamentoId: lanc?.id,
        dataVencimento: lanc?.data_vencimento,
      });
      return [];
    }
    vencimento.setHours(0, 0, 0, 0);

    const diasDiferenca = Math.floor((vencimento - hoje) / (1000 * 60 * 60 * 24));

    const badges = [];

    // Preferir status_calculado vindo do backend (concluida/parcial/pendente)
    const statusPagamento = (lanc.status_calculado || lanc.status || '').toLowerCase();

    // BADGE DE PAGAMENTO
    if (statusPagamento === 'concluida' || statusPagamento === 'pago') {
      badges.push({
        tipo: 'pagamento',
        label: 'Concluída',
        color: 'green'
      });
      // Se pago, não adiciona badge de prazo
      return badges;
    } else if (statusPagamento === 'parcial' || statusPagamento === 'parcialmente_pago') {
      badges.push({
        tipo: 'pagamento',
        label: 'Parcial',
        color: 'yellow'
      });
    }

    // BADGE DE PRAZO (se não estiver completamente pago)
    if (lanc.status !== 'pago') {
      if (diasDiferenca < 0) {
        badges.push({
          tipo: 'prazo',
          label: 'Vencida',
          color: 'red',
          prazo: Math.abs(diasDiferenca),
          prazoLabel: `${Math.abs(diasDiferenca)} ${Math.abs(diasDiferenca) === 1 ? 'dia' : 'dias'}`
        });
      } else if (diasDiferenca === 0) {
        badges.push({
          tipo: 'prazo',
          label: 'Vence Hoje',
          color: 'orange',
          prazo: 0,
          prazoLabel: 'Hoje'
        });
      } else {
        badges.push({
          tipo: 'prazo',
          label: 'Pendente',
          color: 'blue',
          prazo: diasDiferenca,
          prazoLabel: `${diasDiferenca} ${diasDiferenca === 1 ? 'dia' : 'dias'}`
        });
      }
    }

    return badges;
  };

  const applyFilters = () => {
    let filtered = lancamentos;

    // Filtro de busca
    if (searchTerm) {
         const q = searchTerm.toLowerCase();
         filtered = filtered.filter(l => {
          const empresa = (l._empresa || '').toLowerCase(); // << usar o campo normalizado
          return (
            l.descricao?.toLowerCase().includes(q) ||
            l.fornecedor?.toLowerCase().includes(q) ||
            l.cliente?.toLowerCase().includes(q) ||
            empresa.includes(q) ||
            l.nota_numero?.toLowerCase().includes(q)
          );
        });
      }

    // Filtro por período de vencimento (se "from" ou "to" estiverem definidos)
if (dateFilter.from || dateFilter.to) {
  const hasFrom = !!dateFilter.from;
  const hasTo = !!dateFilter.to;
  const start = hasFrom ? startOfDay(parseISO(dateFilter.from)) : null;
  const end   = hasTo   ? endOfDay(parseISO(dateFilter.to))   : null;

  filtered = filtered.filter((l) => {
    if (!l.data_vencimento) return false;
    const d = startOfDay(parseISO(l.data_vencimento));
    if (start && end) return isWithinInterval(d, { start, end });
    if (start && !end) return d >= start;
    if (!start && end) return d <= end;
    return true;
  });
}

    // Verificar se há algum filtro ativo
    const hasTipoFilter = activeFilters.pagar || activeFilters.receber;
    const hasStatusFilter = activeFilters.vencida || activeFilters.vence_hoje || 
                           activeFilters.pendente || activeFilters.parcial || 
                           activeFilters.concluida;

    // Se não há nenhum filtro, mostrar tudo
    if (!hasTipoFilter && !hasStatusFilter) {
      setFilteredLancamentos(filtered);
      return;
    }

    // Aplicar filtros
    filtered = filtered.filter(l => {
      const badges = calcularStatusEPrazo(l);
      
      // Filtro de TIPO
      const matchTipo = !hasTipoFilter || (
        (activeFilters.pagar && l.tipo === 'pagar') ||
        (activeFilters.receber && l.tipo === 'receber')
      );

      // Filtro de STATUS
      const matchStatus = !hasStatusFilter || badges.some(badge => {
        if (activeFilters.vencida && badge.label === 'Vencida') return true;
        if (activeFilters.vence_hoje && badge.label === 'Vence Hoje') return true;
        if (activeFilters.pendente && badge.label === 'Pendente') return true;
        if (activeFilters.parcial && badge.label === 'Parcial') return true;
        if (activeFilters.concluida && badge.label === 'Concluída') return true;
        return false;
      });

      return matchTipo && matchStatus;
    });

    setFilteredLancamentos(filtered);
  };

  const toggleFilter = (filter) => {
    setActiveFilters(prev => ({
      ...prev,
      [filter]: !prev[filter]
    }));
  };

  const handleEdit = (lancamento) => {
    setNewLancamento({
      tipo: lancamento.tipo,
      descricao: lancamento.descricao,
      valor: lancamento.valor,
      data_vencimento: lancamento.data_vencimento,
      fornecedor: lancamento.fornecedor || '',
      cliente: lancamento.cliente || '',
      observacoes: lancamento.observacoes || ''
    });
    setCurrentLancamento(lancamento);
    setShowCreateModal(true);
  };

  const handleCreateLancamento = async (e) => {
    e.preventDefault();
    
    const url = currentLancamento 
      ? `/api/lancamentos/${currentLancamento.id}` 
      : '/api/lancamentos';
      
    const method = currentLancamento ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLancamento)
      });
  
      if (response.ok) {
        setShowCreateModal(false);
        setCurrentLancamento(null);
        setNewLancamento({
          tipo: 'pagar',
          descricao: '',
          valor: '',
          data_vencimento: '',
          fornecedor: '',
          cliente: '',
          observacoes: ''
        });
        fetchLancamentos();
      } else {
        const error = await response.json();
        alert(`Erro: ${error.error || 'Erro ao salvar lançamento'}`);
      }
    } catch (error) {
      console.error('Erro:', error);
      alert('Erro ao salvar lançamento');
    }
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
formData.append('valor', paymentData.valor);
formData.append('data_pagamento', paymentData.data_pagamento);
formData.append('observacoes', paymentData.observacoes);
if (paymentData.comprovante) {
  const f = paymentData.comprovante;
  formData.append('comprovante', f, f.name || 'comprovante');
}


      const response = await fetch(`/api/lancamentos/${currentLancamento.id}/pagamento`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setShowPaymentModal(false);
        setPaymentData({
          valor: '',
          data_pagamento: format(new Date(), 'yyyy-MM-dd'),
          observacoes: '',
          comprovante: null
        });
        fetchLancamentos();
      } else {
        const error = await response.json();
        alert(`Erro: ${error.error || 'Erro ao registrar pagamento'}`);
      }
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      alert('Erro ao registrar pagamento');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Deseja realmente excluir este lançamento?')) return;

    try {
      const response = await fetch(`/api/lancamentos/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchLancamentos();
      } else {
        const error = await response.json();
        alert(`Erro ao deletar: ${error.error}`);
      }
    } catch (error) {
      console.error('Erro ao deletar lançamento:', error);
      alert('Erro ao deletar lançamento');
    }
  };

  const openPaymentModal = (lancamento) => {
    const total = Number(lancamento.valor || 0);
    const pago  = Number(lancamento.total_pago || 0);                            // << usa total_pago
    const pend  = Math.max(0, Number(lancamento.saldo ?? (total - pago)));       // << prefere saldo
    setCurrentLancamento(lancamento);
    setPaymentData(p => ({
      ...p,
      valor: pend ? pend.toFixed(2) : ''
    }));
    setShowPaymentModal(true);
  };
  

  const formatarDescricaoPrincipal = (lanc) => {
    if (lanc.nota_fiscal_id) {
      const nfRef = `NF ${lanc.nota_numero || lanc.nota_fiscal_id}`;
      const parcelaRef = lanc.parcela_numero ? ` - Parcela ${String(lanc.parcela_numero).padStart(3, '0')}` : '';
      return `${nfRef}${parcelaRef}`;
    }
    return lanc.descricao;
  };

  const formatarEmpresa = (lanc) => {
    // preferências diretas
    if (lanc.empresa) return lanc.empresa;
  
    // campos comuns de cadastro manual
    if (lanc.tipo === 'pagar') {
      if (lanc.fornecedor) return lanc.fornecedor;
      if (lanc.fornecedor_nome) return lanc.fornecedor_nome;
      if (lanc.emitente) return lanc.emitente; // quando vem de NF como "outro lado"
      if (lanc.emitente_nome) return lanc.emitente_nome;
    } else { // receber
      if (lanc.cliente) return lanc.cliente;
      if (lanc.cliente_nome) return lanc.cliente_nome;
      if (lanc.destinatario) return lanc.destinatario;
      if (lanc.destinatario_nome) return lanc.destinatario_nome;
    }
  
    // outras variantes que já vires no seu payload
    if (lanc.entidade) return lanc.entidade;
    if (lanc.parte_oposta) return lanc.parte_oposta;
    if (lanc.contraparte) return lanc.contraparte;
  
    return 'Não informado';
  };
  

  const statusColors = {
    green:
      'bg-success-100 text-success-700 border-success-200 dark:bg-success-900/40 dark:text-success-200 dark:border-success-700',
    red:
      'bg-danger-100 text-danger-700 border-danger-200 dark:bg-danger-900/40 dark:text-danger-200 dark:border-danger-700',
    yellow:
      'bg-warning-100 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-200 dark:border-warning-700',
    orange:
      'bg-warning-100 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-200 dark:border-warning-700',
    blue:
      'bg-primary-100 text-primary-700 border-primary-200 dark:bg-primary-900/40 dark:text-primary-200 dark:border-primary-700'
  };

  const tipoColors = {
    pagar:
      'bg-danger-100 text-danger-700 border-danger-200 dark:bg-danger-900/40 dark:text-danger-200 dark:border-danger-700',
    receber:
      'bg-success-100 text-success-700 border-success-200 dark:bg-success-900/40 dark:text-success-200 dark:border-success-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
  <div>
    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Lançamentos</h1>
    <p className="text-gray-600 dark:text-gray-400 mt-1">Gerencie contas a pagar e receber</p>
  </div>
  <button
  onClick={() => setShowCreateModal(true)}
  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
>
  <Plus size={20} />
  Novo Lançamento
</button>
      </div>

      {/* Barra de Pesquisa e Filtros (ÚNICO cabeçalho) */}
<div className="card p-4 space-y-4">
  {/* Linha 1: Busca */}
  <div className="flex gap-4">
    <div className="flex-1 relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
      <input
        type="text"
        placeholder="Buscar lançamentos..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="input-field pl-10"
      />
    </div>
  </div>

  {/* Linha 2: Tipo + Status (mesma linha) */}
  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
    {/* Tipo de Operação */}
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium mr-1 text-gray-600 dark:text-gray-400">Tipo</span>
      <button
        onClick={() => toggleFilter('pagar')}
        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
          activeFilters.pagar
            ? 'bg-danger-100 text-danger-700 border-danger-200 hover:bg-danger-200 dark:bg-danger-900/40 dark:text-danger-200 dark:border-danger-700 dark:hover:bg-danger-800'
            : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700'
        }`}
        title="A Pagar"
      >
        <span className="inline-flex items-center gap-1.5"><ArrowUp size={14}/> A Pagar</span>
      </button>
      <button
        onClick={() => toggleFilter('receber')}
        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
          activeFilters.receber
            ? 'bg-success-100 text-success-700 border-success-200 hover:bg-success-200 dark:bg-success-900/40 dark:text-success-200 dark:border-success-700 dark:hover:bg-success-800'
            : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700'
        }`}
        title="A Receber"
      >
        <span className="inline-flex items-center gap-1.5"><ArrowDown size={14}/> A Receber</span>
      </button>
    </div>

    {/* Status (ao lado) */}
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium mr-1 text-gray-600 dark:text-gray-400">Status</span>
      {[
        { key: 'vencida',    label: 'Vencida' },
        { key: 'vence_hoje', label: 'Vence Hoje' },
        { key: 'pendente',   label: 'Pendente' },
        { key: 'parcial',    label: 'Parcial' },
        { key: 'concluida',  label: 'Concluída' },
      ].map((s) => (
        <button
          key={s.key}
          onClick={() => toggleFilter(s.key)}
          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            activeFilters[s.key]
              ? 'border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/40 dark:text-primary-200 dark:border-primary-500/60 dark:hover:bg-primary-900/60'
              : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700'
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  </div>

    {/* Linha 3: Período (presets + único calendário) */}
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
    {/* Presets compactos */}
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium mr-1 text-gray-600 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <Calendar size={14}/> Período
        </span>
      </span>
      {[
        { k: '7d',  label: 'Últimos 7d' },
        { k: '15d', label: 'Últimos 15d' },
        { k: '30d', label: 'Últimos 30d' },
        { k: 'mes', label: 'Mês atual' },
      ].map(p => (
        <button
          key={p.k}
          onClick={() => applyQuickRange(p.k)}
          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            quickRange === p.k
              ? 'border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/40 dark:text-primary-200 dark:border-primary-500/60 dark:hover:bg-primary-900/60'
              : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>

    {/* Trigger + Popover do calendário (sobreposto) */}
<div className="relative">
  <button
    type="button"
    onClick={() => setShowCalendar((v) => !v)}
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors
               bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
    title="Selecionar período"
  >
    <Calendar size={16} />
    {dateFilter.from
      ? `${format(parseISO(dateFilter.from), 'dd/MM/yyyy', { locale: ptBR })} → ${dateFilter.to ? format(parseISO(dateFilter.to), 'dd/MM/yyyy', { locale: ptBR }) : format(parseISO(dateFilter.from), 'dd/MM/yyyy', { locale: ptBR })}`
      : 'Selecionar datas'}
  </button>

  {showCalendar && (
    <div
      ref={calRef}
      className="absolute right-0 mt-2 z-50 w-[320px] sm:w-[360px]
                 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl"
    >
      <div className="p-3">
        <RangeCalendar
  from={dateFilter.from}
  to={dateFilter.to}
  onApply={({ from, to }) => {
    setQuickRange(null);                 // sair de preset
    setDateFilter({ from, to });         // aplica o período
    setShowCalendar(false);              // fecha ao aplicar
  }}
  onClear={() => {
    // LIMPAR = sem filtro (mostrar tudo) e já aplicar
    setQuickRange(null);
    setDateFilter({ from: '', to: '' });
    setShowCalendar(false);
  }}
/>

      </div>
    </div>
  )}
</div>
  </div>
</div>  {/* <-- fecha APENAS o card de filtros */}


      {/* Tabela */}
      <div className="card overflow-hidden">
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left p-4 whitespace-nowrap">Tipo</th>
                <th className="text-left p-4 whitespace-nowrap">Descrição</th>
                <th className="text-left p-4 whitespace-nowrap">Empresa/Entidade</th>
                <th className="text-left p-4 whitespace-nowrap">Vencimento</th>
                <th className="text-right p-4 whitespace-nowrap">Valor</th>
                <th className="text-center p-4 whitespace-nowrap">Status</th>
                <th className="text-center p-4 whitespace-nowrap">Prazo</th>
                <th className="text-right p-4 whitespace-nowrap">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredLancamentos.map((lanc) => {
                const badges = calcularStatusEPrazo(lanc);
                const empresa = lanc._empresa || 'Não informado';
                const total = Number(lanc.valor || 0);
const valorPago = Number(lanc.total_pago ?? lanc.valor_pago ?? 0);
const valorPendente = Number(lanc.saldo ?? Math.max(0, total - valorPago));

// Seleciona badges já calculados
let badgePagamento = badges.find(b => b.tipo === 'pagamento');
const badgePrazo   = badges.find(b => b.tipo === 'prazo');

// Se o backend não trouxe, infere pelo total/pago
if (!badgePagamento) {
  if (valorPendente <= 0 && total > 0) {
    badgePagamento = { tipo: 'pagamento', label: 'Concluída', color: 'green' };
  } else if (valorPago > 0 && valorPendente > 0) {
    badgePagamento = { tipo: 'pagamento', label: 'Parcial', color: 'yellow' };
  }
}


                const isFocused =
                  focusedLancamentoId && String(focusedLancamentoId) === String(lanc.id);


                return (
                  <tr
                    key={lanc.id}
                    data-focused={isFocused ? 'true' : undefined}
                    className={`border-b border-gray-200 dark:border-gray-700 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      isFocused ? 'bg-primary-50 dark:bg-primary-500/10 ring-1 ring-primary-500/50' : ''
                    }`}
                  >
                    {/* TIPO */}
                    <td className="p-4">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 whitespace-nowrap ${tipoColors[lanc.tipo]}`}>
                        {lanc.tipo === 'pagar' ? (
                          <>
                            <ArrowUp size={12} />
                            <span>A Pagar</span>
                          </>
                        ) : (
                          <>
                            <ArrowDown size={12} />
                            <span>A Receber</span>
                          </>
                        )}
                      </span>
                    </td>
                    
                    {/* DESCRIÇÃO */}
                    <td className="p-4">
                      <div className="font-medium">{formatarDescricaoPrincipal(lanc)}</div>
                    </td>
                    
                    {/* EMPRESA */}
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Building size={16} className="text-gray-400 flex-shrink-0" />
                        <span className={`text-sm ${empresa === 'Não informado' ? 'text-gray-500 italic' : ''}`}>
                          {empresa}
                        </span>
                      </div>
                    </td>
                    
                    {/* VENCIMENTO */}
                    <td className="p-4 whitespace-nowrap">
                      {format(parseISO(lanc.data_vencimento), 'dd/MM/yyyy', { locale: ptBR })}
                    </td>
                    
                    {/* VALOR */}
                    <td className="p-4 text-right font-mono whitespace-nowrap">
                      {/* valor grande: saldo se parcial, senão total */}
                      <div
                        className={`font-bold ${
                          valorPago > 0 && valorPendente > 0
                            ? 'text-warning-600 dark:text-warning-200'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {fmtBRL(
                          valorPago > 0 && valorPendente > 0 ? valorPendente : total
                        )}
                      </div>

                      {/* linhas menores: Total e Pago */}
                      <div className="mt-1 text-[11px] font-mono space-y-0.5 text-gray-600 dark:text-gray-300">
                        <div>
                          Total:{' '}
                          <span className="text-gray-900 dark:text-gray-100">{fmtBRL(total)}</span>
                        </div>
                        {valorPago > 0 && (
                          <div className="text-success-600 dark:text-success-300">
                            Pago: {fmtBRL(valorPago)}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    {/* STATUS */}
                    <td className="p-4 text-center">
                      {badgePagamento ? (
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-block whitespace-nowrap ${statusColors[badgePagamento.color]}`}>
                          {badgePagamento.label}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                
                    </td>
                    
                    {/* PRAZO */}
                    <td className="p-4 text-center">
                      {badgePrazo ? (
                        <div className="space-y-1">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-block whitespace-nowrap ${statusColors[badgePrazo.color]}`}>
                            {badgePrazo.label}
                          </span>
                          {badgePrazo.prazo !== null && (
                            <div
                              className={`text-xs font-medium ${
                                badgePrazo.color === 'red'
                                  ? 'text-danger-600 dark:text-danger-200'
                                  : badgePrazo.color === 'orange'
                                  ? 'text-warning-600 dark:text-warning-200'
                                  : 'text-gray-600 dark:text-gray-300'
                              }`}
                            >
                              {badgePrazo.prazoLabel}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    
                    {/* AÇÕES */}
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedLancamento(lanc);
                            setShowDetailModal(true);
                          }}
                          className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors text-blue-400"
                          title="Ver detalhes"
                        >
                          <Eye size={18} />
                        </button>
                        {lanc.status !== 'pago' && (
                          <>
                            <button
                              onClick={() => handleEdit(lanc)}
                              className="p-2 hover:bg-yellow-500/20 rounded-lg transition-colors text-yellow-400"
                              title="Editar"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => openPaymentModal(lanc)}
                              className="p-2 hover:bg-green-500/20 rounded-lg transition-colors text-green-400"
                              title="Registrar pagamento"
                            >
                              <DollarSign size={18} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(lanc.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                          title="Excluir"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredLancamentos.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            Nenhum lançamento encontrado
          </div>
        )}
      </div>

      {/* Modal de Criar/Editar Lançamento */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-2xl w-full p-6 shadow-xl border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {currentLancamento ? 'Editar Lançamento' : 'Novo Lançamento'}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="icon-button">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateLancamento} className="space-y-4 text-gray-700 dark:text-gray-300">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo</label>
                  <select
                    value={newLancamento.tipo}
                    onChange={(e) => setNewLancamento({ ...newLancamento, tipo: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="pagar">A Pagar</option>
                    <option value="receber">A Receber</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Valor</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newLancamento.valor}
                    onChange={(e) => setNewLancamento({ ...newLancamento, valor: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descrição</label>
                <input
                  type="text"
                  value={newLancamento.descricao}
                  onChange={(e) => setNewLancamento({ ...newLancamento, descricao: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data de Vencimento</label>
                  <input
                    type="date"
                    value={newLancamento.data_vencimento}
                    onChange={(e) => setNewLancamento({ ...newLancamento, data_vencimento: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>

                {newLancamento.tipo === 'pagar' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fornecedor</label>
                    <input
                      type="text"
                      value={newLancamento.fornecedor}
                      onChange={(e) => setNewLancamento({ ...newLancamento, fornecedor: e.target.value })}
                      className="input-field"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cliente</label>
                    <input
                      type="text"
                      value={newLancamento.cliente}
                      onChange={(e) => setNewLancamento({ ...newLancamento, cliente: e.target.value })}
                      className="input-field"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observações</label>
                <textarea
                  value={newLancamento.observacoes}
                  onChange={(e) => setNewLancamento({ ...newLancamento, observacoes: e.target.value })}
                  className="input-field min-h-[96px]"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {currentLancamento ? 'Salvar Alterações' : 'Criar Lançamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Pagamento */}
      {showPaymentModal && currentLancamento && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-md w-full p-6 shadow-xl border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Registrar Pagamento</h2>
              <button onClick={() => setShowPaymentModal(false)} className="icon-button">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleRegisterPayment} className="space-y-4 text-gray-700 dark:text-gray-300">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Valor</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentData.valor}
                  onChange={(e) => setPaymentData({ ...paymentData, valor: e.target.value })}
                  className="input-field"
                  required
                />
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Valor do lançamento: R$ {Number(currentLancamento.valor).toFixed(2)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data do Pagamento</label>
                <input
                  type="date"
                  value={paymentData.data_pagamento}
                  onChange={(e) => setPaymentData({ ...paymentData, data_pagamento: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Comprovante (opcional)</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setPaymentData({ ...paymentData, comprovante: e.target.files[0] })}
                  className="input-field file:mr-4 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/30 dark:file:text-primary-200 dark:hover:file:bg-primary-900/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observações</label>
                <textarea
                  value={paymentData.observacoes}
                  onChange={(e) => setPaymentData({ ...paymentData, observacoes: e.target.value })}
                  className="input-field min-h-[96px]"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-success"
                >
                  Registrar Pagamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {showDetailModal && selectedLancamento && (
        <LancamentoDetailModal
          lancamento={selectedLancamento}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedLancamento(null);
          }}
          onReload={fetchLancamentos}
          onOpenPayment={(lanc) => {
            setShowDetailModal(false);
            openPaymentModal(lanc);
          }}
        />
      )}
    </div>
  );
};

function RangeCalendar({ from, to, onApply, onClear }) {
  const [viewMonth, setViewMonth] = useState(from ? parseISO(from) : new Date());
  const [tFrom, setTFrom] = useState(from || '');
  const [tTo, setTTo] = useState(to || '');

  // constrói grade do mês (semana começa no domingo; ajuste se quiser)
  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);

  const days = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) {
    days.push(d);
  }

  const handleDayClick = (dateObj) => {
    const d = format(dateObj, 'yyyy-MM-dd');
    // 1º clique: define início
    if (!tFrom || (tFrom && tTo)) {
      setTFrom(d);
      setTTo('');
      return;
    }
    // 2º clique: define fim (swap se necessário) — clique 2x no mesmo dia => período de 1 dia
    if (tFrom && !tTo) {
      if (parseISO(d) < parseISO(tFrom)) {
        setTTo(tFrom);
        setTFrom(d);
      } else {
        setTTo(d);
      }
    }
  };

  const inTempRange = (dateObj) => {
    if (!tFrom) return false;
    const s = startOfDay(parseISO(tFrom));
    const e = tTo ? endOfDay(parseISO(tTo)) : s;
    return isWithinInterval(dateObj, { start: s, end: e });
  };

  const isStart = (dateObj) => tFrom && isSameDay(dateObj, parseISO(tFrom));
  const isEnd   = (dateObj) => tTo && isSameDay(dateObj, parseISO(tTo));

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* header mês */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setViewMonth(addMonths(viewMonth, -1))}
          className="px-2 py-1 rounded border text-sm font-medium transition-colors bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
        >
          ‹
        </button>
        <div className="text-sm font-medium">
          {format(viewMonth, "MMMM yyyy", { locale: ptBR })}
        </div>
        <button
          onClick={() => setViewMonth(addMonths(viewMonth, 1))}
          className="px-2 py-1 rounded border text-sm font-medium transition-colors bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
        >
          ›
        </button>
      </div>

      {/* cabeçalho dias da semana */}
      <div className="grid grid-cols-7 text-xs text-gray-500 dark:text-gray-400 mb-1">
        {['D','S','T','Q','Q','S','S'].map((w) => (
          <div key={w} className="text-center py-1">{w}</div>
        ))}
      </div>

      {/* grade de dias */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const muted = !isSameMonth(d, viewMonth);
          const selected = inTempRange(d);
          const start = isStart(d);
          const end = isEnd(d);
          return (
            <button
              key={d.toISOString()}
              onClick={() => handleDayClick(d)}
              className={[
                "text-sm h-9 rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/70",
                muted
                  ? "text-gray-400 border-gray-200 dark:text-gray-500 dark:border-gray-700"
                  : "text-gray-700 border-gray-200 dark:text-gray-100 dark:border-gray-700",
                selected
                  ? "bg-primary-100 text-primary-700 border-primary-300 dark:bg-primary-900/40 dark:text-primary-200 dark:border-primary-500/40"
                  : "bg-white hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800",
                (start || end) ? "ring-2 ring-primary-500 dark:ring-primary-400" : ""
              ].join(" ")}
              title={format(d, 'dd/MM/yyyy')}
            >
              {format(d, 'd')}
            </button>
          );
        })}
      </div>

      {/* rodapé: seleção atual + botões */}
      <div className="mt-3 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
        <div>
          {(tFrom ? format(parseISO(tFrom), 'dd/MM/yyyy', { locale: ptBR }) : '—')}
          {' '}→{' '}
          {(tTo ? format(parseISO(tTo), 'dd/MM/yyyy', { locale: ptBR }) : (tFrom ? format(parseISO(tFrom), 'dd/MM/yyyy', { locale: ptBR }) : '—'))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              // zera seleção visual do calendário
              setTFrom('');
              setTTo('');
              // comunica ao pai para REMOVER o filtro (mostrar tudo)
              if (onClear) onClear();
            }}
            className="px-3 py-1 rounded border text-sm font-medium transition-colors bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Limpar
          </button>

          <button
            onClick={() => {
              // sem fim -> um dia só
              const appliedFrom = tFrom || '';
              const appliedTo = tTo || tFrom || '';
              onApply({ from: appliedFrom, to: appliedTo });
            }}
            disabled={!tFrom}
            className={`px-3 py-1 rounded border text-sm font-medium transition-colors ${
              tFrom
                ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-500"
                : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700"
            }`}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}


export default LancamentosV2;
